/* global window, document, fetch, Survey */

(function (window, document) {
    var MAX_API_LOG = 20;
    var MAX_ERR_LOG = 20;
    var MAX_UI_LOG = 15;
    var apiLog = [];
    var errLog = [];
    var uiLog = [];
    var diagnosticsInstalled = false;
    var _fetchWrapped = false;
    var _xhrWrapped = false;
    var _tierCache = null;
    /** @type {File[]} Files kept client-side until POST /support/bug-reports */
    var _bugReportAttachmentFiles = [];

    function pushRing(buf, item, max) {
        buf.push(item);
        if (buf.length > max) buf.shift();
    }

    function htmlEl() {
        return document.documentElement;
    }

    function apiBase() {
        var el = htmlEl();
        var base = (el && el.getAttribute('data-api-base')) || '';
        base = String(base || '').trim();
        return base ? base.replace(/\/+$/, '') : '/api';
    }

    function safeSelector(el) {
        if (!el || !el.tagName) return '';
        var tag = String(el.tagName).toLowerCase();
        var id = el.id ? '#' + el.id : '';
        var cls = el.className && typeof el.className === 'string'
            ? '.' + el.className.trim().split(/\s+/).slice(0, 3).join('.')
            : '';
        return tag + id + cls;
    }

    function describeActiveElement() {
        var el = document.activeElement;
        if (!el || el === document.body) return null;
        var tag = String(el.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') {
            var type = (el.getAttribute('type') || '').toLowerCase();
            if (type === 'password') {
                return { tag: tag, type: type, name: el.name || null, id: el.id || null };
            }
        }
        return {
            tag: tag,
            id: el.id || null,
            name: el.name || null,
            type: el.getAttribute ? el.getAttribute('type') : null
        };
    }

    function storageKeyNames(storage) {
        var keys = [];
        try {
            for (var i = 0; i < storage.length; i++) keys.push(storage.key(i));
        } catch (e) {}
        return keys;
    }

    function detectFeatures() {
        var out = {};
        try {
            var c = document.createElement('canvas');
            out.webgl = !!(c.getContext('webgl') || c.getContext('experimental-webgl'));
        } catch (e1) {
            out.webgl = false;
        }
        out.webgpu = !!(navigator.gpu);
        out.clipboard = !!(navigator.clipboard && navigator.clipboard.writeText);
        out.geolocation = !!navigator.geolocation;
        out.serviceWorker = 'serviceWorker' in navigator;
        return out;
    }

    function installDiagnostics() {
        if (diagnosticsInstalled) return;
        diagnosticsInstalled = true;

        window.addEventListener('error', function (ev) {
            pushRing(errLog, {
                kind: 'error',
                message: ev.message || String(ev.error || ''),
                filename: ev.filename || null,
                lineno: ev.lineno || null,
                colno: ev.colno || null,
                stack: ev.error && ev.error.stack ? String(ev.error.stack) : null,
                at: Date.now()
            }, MAX_ERR_LOG);
        });

        window.addEventListener('unhandledrejection', function (ev) {
            var reason = ev.reason;
            pushRing(errLog, {
                kind: 'unhandledrejection',
                message: reason && reason.message ? String(reason.message) : String(reason || ''),
                stack: reason && reason.stack ? String(reason.stack) : null,
                at: Date.now()
            }, MAX_ERR_LOG);
        });

        document.addEventListener('click', function (ev) {
            var t = ev.target;
            pushRing(uiLog, { kind: 'click', selector: safeSelector(t), at: Date.now() }, MAX_UI_LOG);
        }, true);

        document.addEventListener('change', function (ev) {
            var t = ev.target;
            pushRing(uiLog, { kind: 'change', selector: safeSelector(t), at: Date.now() }, MAX_UI_LOG);
        }, true);

        if (!_fetchWrapped && typeof window.fetch === 'function') {
            _fetchWrapped = true;
            var origFetch = window.fetch;
            window.fetch = function (input, init) {
                var url = typeof input === 'string' ? input : (input && input.url) || '';
                var method = (init && init.method) || (input && input.method) || 'GET';
                var started = Date.now();
                return origFetch.apply(this, arguments).then(function (res) {
                    var rid = res.headers && res.headers.get ? res.headers.get('X-Request-Id') : null;
                    pushRing(apiLog, {
                        endpoint: url,
                        method: String(method).toUpperCase(),
                        status: res.status,
                        ms: Date.now() - started,
                        requestId: rid,
                        at: Date.now()
                    }, MAX_API_LOG);
                    return res;
                }).catch(function (err) {
                    pushRing(apiLog, {
                        endpoint: url,
                        method: String(method).toUpperCase(),
                        status: null,
                        ms: Date.now() - started,
                        error: err && err.message ? String(err.message) : String(err || 'fetch error'),
                        at: Date.now()
                    }, MAX_API_LOG);
                    throw err;
                });
            };
        }

        if (!_xhrWrapped && window.XMLHttpRequest) {
            _xhrWrapped = true;
            var XHR = window.XMLHttpRequest;
            var origOpen = XHR.prototype.open;
            var origSend = XHR.prototype.send;
            XHR.prototype.open = function (method, url) {
                this.__vgBugMethod = method;
                this.__vgBugUrl = url;
                this.__vgBugStarted = 0;
                return origOpen.apply(this, arguments);
            };
            XHR.prototype.send = function () {
                var self = this;
                self.__vgBugStarted = Date.now();
                self.addEventListener('loadend', function () {
                    pushRing(apiLog, {
                        endpoint: self.__vgBugUrl || '',
                        method: String(self.__vgBugMethod || 'GET').toUpperCase(),
                        status: self.status,
                        ms: Date.now() - (self.__vgBugStarted || Date.now()),
                        at: Date.now()
                    }, MAX_API_LOG);
                });
                return origSend.apply(this, arguments);
            };
        }
    }

    function collectPerformance() {
        var out = {};
        try {
            var nav = performance.getEntriesByType && performance.getEntriesByType('navigation');
            if (nav && nav[0]) {
                var n = nav[0];
                out.navigation = {
                    domContentLoaded: Math.round(n.domContentLoadedEventEnd || 0),
                    loadEventEnd: Math.round(n.loadEventEnd || 0),
                    duration: Math.round(n.duration || 0)
                };
            } else if (performance.timing) {
                var t = performance.timing;
                out.timing = {
                    loadEventEnd: t.loadEventEnd - t.navigationStart,
                    domComplete: t.domComplete - t.navigationStart
                };
            }
        } catch (e0) {}
        try {
            if (performance.memory) {
                out.memory = {
                    jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
                    totalJSHeapSize: performance.memory.totalJSHeapSize,
                    usedJSHeapSize: performance.memory.usedJSHeapSize
                };
            }
        } catch (e1) {}
        try {
            var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
            if (conn) {
                out.connection = {
                    effectiveType: conn.effectiveType || null,
                    downlink: conn.downlink,
                    rtt: conn.rtt,
                    saveData: conn.saveData
                };
            }
        } catch (e2) {}
        return out;
    }

    function collectRadarContext() {
        var root = document.querySelector('[id^="radar-"][id$="-root"]');
        if (!root) return null;
        var ctx = {
            rootId: root.id,
            path: window.location.pathname,
            title: document.title
        };
        var form = root.querySelector('[data-radar-filters]');
        if (form) {
            var filters = {};
            try {
                var fd = new FormData(form);
                fd.forEach(function (val, key) {
                    if (filters[key] !== undefined) {
                        if (!Array.isArray(filters[key])) filters[key] = [filters[key]];
                        filters[key].push(val);
                    } else {
                        filters[key] = val;
                    }
                });
            } catch (e) {}
            ctx.filters = filters;
        }
        var table = root._vgTabulator;
        if (table && typeof table.getPage === 'function') {
            ctx.pagination = {
                page: table.getPage(),
                pageSize: table.getPageSize ? table.getPageSize() : null
            };
            try {
                var sorters = table.getSorters ? table.getSorters() : [];
                ctx.sort = (sorters || []).map(function (s) {
                    return { field: s.field, dir: s.dir };
                });
            } catch (e2) {}
        }
        var companyEl = root.querySelector('[data-radar-company], [name="company_uuid"], #radar-company-uuid');
        if (companyEl) ctx.selectedCompany = companyEl.value || companyEl.textContent || null;
        var buyerEl = root.querySelector('[data-radar-buyer], [name="buyer"], #radar-buyer-buyer-list input:checked');
        if (buyerEl) ctx.selectedBuyer = buyerEl.value || null;
        return ctx;
    }

    function collectMapContext() {
        if (window.AppaltiMap && typeof window.AppaltiMap.getBugReportMapContext === 'function') {
            return window.AppaltiMap.getBugReportMapContext();
        }
        return { available: false };
    }

    function collectUserContext() {
        var el = htmlEl();
        var uuid = el.getAttribute('data-member-uuid') || null;
        var paid = el.getAttribute('data-member-paid');
        return {
            loggedIn: !!uuid,
            paid: paid === 'true' || paid === true,
            memberUuid: uuid,
            tierName: _tierCache || null
        };
    }

    function fetchTierIfLoggedIn() {
        var el = htmlEl();
        if (!el.getAttribute('data-member-uuid')) return Promise.resolve(null);
        return fetch('/members/api/session/', { method: 'GET', credentials: 'include' })
            .then(function (r) {
                if (r.status === 204 || !r.ok) return null;
                return r.text();
            })
            .then(function (token) {
                token = (token || '').trim();
                if (!token) return null;
                return fetch(apiBase() + '/auth/ghost/session/verify', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({ session_token: token })
                }).then(function (r2) {
                    return r2.json();
                });
            })
            .then(function (data) {
                if (!data || !data.valid) return null;
                var tiers = data.plans && data.plans.active_tiers ? data.plans.active_tiers : [];
                if (tiers.length && tiers[0]) {
                    return tiers[0].name || tiers[0].slug || null;
                }
                if (data.plans && data.plans.is_paid) return 'paid';
                if (data.member && data.member.paid) return 'paid';
                return 'free';
            })
            .catch(function () {
                return null;
            });
    }

    function collectContext() {
        var el = htmlEl();
        return {
            schemaVersion: 1,
            collectedAt: new Date().toISOString(),
            deploy: {
                version: el.getAttribute('data-vg-version') || null,
                build: el.getAttribute('data-vg-build') || null,
                env: el.getAttribute('data-vg-env') || 'prod'
            },
            page: {
                href: window.location.href,
                pathname: window.location.pathname,
                search: window.location.search,
                hash: window.location.hash,
                referrer: document.referrer || null,
                title: document.title,
                visibilityState: document.visibilityState
            },
            browser: {
                userAgent: navigator.userAgent,
                language: navigator.language,
                languages: navigator.languages ? Array.prototype.slice.call(navigator.languages) : [],
                platform: navigator.platform,
                timezone: (function () {
                    try {
                        return Intl.DateTimeFormat().resolvedOptions().timeZone;
                    } catch (e) {
                        return null;
                    }
                })(),
                viewport: { width: window.innerWidth, height: window.innerHeight },
                screen: {
                    width: window.screen.width,
                    height: window.screen.height,
                    devicePixelRatio: window.devicePixelRatio
                },
                scroll: { x: window.scrollX, y: window.scrollY },
                activeElement: describeActiveElement()
            },
            user: collectUserContext(),
            map: collectMapContext(),
            radar: collectRadarContext(),
            diagnostics: {
                jsErrors: errLog.slice(),
                apiCalls: apiLog.slice(),
                performance: collectPerformance(),
                localStorageKeys: storageKeyNames(window.localStorage),
                sessionStorageKeys: storageKeyNames(window.sessionStorage),
                features: detectFeatures(),
                uiHistory: uiLog.slice()
            }
        };
    }

    function surveyJson() {
        return {
            showProgressBar: 'top',
            progressBarType: 'pages',
            progressBarShowPageTitles: true,
            progressBarShowPageNumbers: true,
            showQuestionNumbers: 'off',
            showNavigationButtons: false,
            showCompleteButton: false,
            pagePrevText: 'Indietro',
            pageNextText: 'Avanti',
            pages: [
                {
                    name: 'cosa',
                    title: 'Cosa è successo',
                    description:
                        'Racconta il problema in modo chiaro: un titolo breve e una descrizione con i passi che hai fatto e il risultato che ti aspettavi.',
                    elements: [
                        {
                            type: 'text',
                            name: 'title',
                            title: 'Titolo',
                            isRequired: true,
                            maxLength: 200,
                            placeholder: 'Es. La mappa non carica i cluster'
                        },
                        {
                            type: 'comment',
                            name: 'description',
                            title: 'Descrizione',
                            isRequired: true,
                            rows: 6,
                            placeholder:
                                'Cosa stavi facendo, cosa è andato storto, risultato atteso vs ottenuto. Se puoi, indica i passi per riprodurre il problema.'
                        }
                    ]
                },
                {
                    name: 'classificazione',
                    title: 'Tipo di problema',
                    description:
                        'Aiutaci a indirizzare la segnalazione al team giusto indicando gravità e l’area del sito coinvolta.',
                    elements: [
                        {
                            type: 'radiogroup',
                            name: 'severity',
                            title: 'Gravità',
                            isRequired: true,
                            choices: [
                                { value: 'blocker', text: 'Bloccante — non riesco a usare la funzione' },
                                { value: 'major', text: 'Importante — funziona male ma posso proseguire' },
                                { value: 'minor', text: 'Minore — fastidio limitato' },
                                { value: 'cosmetic', text: 'Estetico — aspetto o testi' }
                            ],
                            defaultValue: 'major'
                        },
                        {
                            type: 'dropdown',
                            name: 'category',
                            title: 'Area del sito',
                            isRequired: true,
                            choices: [
                                { value: 'mappa', text: 'Mappa (homepage / esplora gare)' },
                                { value: 'radar', text: 'Radar (analisi, classifiche, densità…)' },
                                { value: 'profilo', text: 'Company profile' },
                                { value: 'kanban', text: 'Kanban / workspace' },
                                { value: 'login', text: 'Login, account, abbonamento' },
                                { value: 'altro', text: 'Altro' }
                            ],
                            defaultValue: 'altro'
                        }
                    ]
                },
                {
                    name: 'allegati',
                    title: 'Allegati e invio',
                    description:
                        'Puoi allegare screenshot o PDF (solo questi formati). I file vengono controllati da antivirus prima dell’invio. I dati tecnici della pagina ci aiutano a capire il contesto: puoi disattivarli se preferisci.',
                    elements: [
                        {
                            type: 'file',
                            name: 'attachments',
                            title: 'Allegati (opzionale)',
                            allowMultiple: true,
                            acceptedTypes:
                                'image/*,application/pdf,.pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.heif',
                            storeDataAsText: false,
                            maxSize: 10485760,
                            waitForUpload: true
                        },
                        {
                            type: 'boolean',
                            name: 'include_diagnostics',
                            title: 'Includi dati tecnici di diagnostica (consigliato)',
                            description:
                                'URL, browser, stato mappa/radar ed eventuali errori recenti. Non includiamo password o token.',
                            defaultValue: true
                        }
                    ]
                }
            ]
        };
    }

    function setModalLock(on) {
        document.documentElement.classList.toggle('vg-kanban-modal-lock', !!on);
    }

    function escHtml(s) {
        if (s == null) return '';
        return String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    function fileFromSurveyUploadItem(item) {
        if (!item) return null;
        if (item instanceof File) return item;
        if (item.file instanceof File) return item.file;
        if (item.content instanceof File) return item.content;
        if (item.content instanceof Blob && item.name) {
            try {
                return new File([item.content], item.name, {
                    type: item.type || item.content.type || 'application/octet-stream'
                });
            } catch (e) {
                return null;
            }
        }
        return null;
    }

    function extractFilesFromSurvey(data) {
        var files = [];
        var seen = new Set();

        function add(f) {
            if (!f || !(f instanceof File)) return;
            var key = f.name + ':' + f.size + ':' + f.lastModified;
            if (seen.has(key)) return;
            seen.add(key);
            files.push(f);
        }

        _bugReportAttachmentFiles.forEach(add);

        var raw = data && data.attachments;
        if (!raw) return files;
        var list = Array.isArray(raw) ? raw : [raw];
        list.forEach(function (item) {
            add(fileFromSurveyUploadItem(item));
        });
        return files;
    }

    function wireSurveyFileHandlers(survey) {
        if (!survey || !survey.onUploadFiles || typeof survey.onUploadFiles.add !== 'function') {
            return;
        }

        survey.onUploadFiles.add(function (_sender, options) {
            var incoming = options && options.files ? options.files : [];
            var allowedExt = {
                pdf: true, png: true, jpg: true, jpeg: true, gif: true, webp: true, heic: true, heif: true
            };
            var rejected = [];
            var stored = [];
            incoming.forEach(function (file) {
                var name = String(file.name || '').toLowerCase();
                var ext = name.indexOf('.') >= 0 ? name.split('.').pop() : '';
                var mime = String(file.type || '').toLowerCase();
                var ok = allowedExt[ext] || mime === 'application/pdf' || mime.indexOf('image/') === 0;
                if (!ok) {
                    rejected.push(file.name || 'file');
                    return;
                }
                _bugReportAttachmentFiles.push(file);
                stored.push({
                    file: file,
                    name: file.name,
                    type: file.type || 'application/octet-stream',
                    content: file
                });
            });
            if (rejected.length) {
                try {
                    options.callback([], [
                        'Formato non consentito (solo immagini o PDF): ' + rejected.join(', ')
                    ]);
                } catch (e0) {
                    try {
                        options.callback('error', [], [
                            'Formato non consentito (solo immagini o PDF): ' + rejected.join(', ')
                        ]);
                    } catch (e1) { /* ignore */ }
                }
                return;
            }
            try {
                if (typeof options.callback === 'function') {
                    options.callback(stored);
                }
            } catch (e1) {
                try {
                    options.callback('success', stored);
                } catch (e2) {
                    try {
                        options.callback([], [String((e2 && e2.message) || 'Upload failed')]);
                    } catch (e3) { /* ignore */ }
                }
            }
        });

        if (survey.onClearFiles && typeof survey.onClearFiles.add === 'function') {
            survey.onClearFiles.add(function (_sender, options) {
                var toRemove = options && options.value ? options.value : [];
                if (!Array.isArray(toRemove)) toRemove = [toRemove];
                toRemove.forEach(function (item) {
                    var f = fileFromSurveyUploadItem(item);
                    if (!f) return;
                    _bugReportAttachmentFiles = _bugReportAttachmentFiles.filter(function (x) {
                        return !(x.name === f.name && x.size === f.size && x.lastModified === f.lastModified);
                    });
                });
                try {
                    if (options && typeof options.callback === 'function') {
                        options.callback('success');
                    }
                } catch (e) { /* ignore */ }
            });
        }
    }

    function open() {
        installDiagnostics();
        if (!window.Survey || !window.Survey.Model) {
            window.alert('Modulo segnalazione non disponibile (SurveyJS). Ricarica la pagina.');
            return;
        }

        fetchTierIfLoggedIn().then(function (tier) {
            if (tier) _tierCache = tier;
        });

        var existing = document.getElementById('vg-bug-report-modal');
        if (existing) existing.remove();

        var wrap = document.createElement('div');
        wrap.id = 'vg-bug-report-modal';
        wrap.className = 'vg-kanban-modal vg-bug-report-modal';
        wrap.innerHTML =
            '<div class="vg-kanban-modal__panel" role="dialog" aria-modal="true">' +
            '  <div class="vg-bug-report-modal__form" data-bug-report-form>' +
            '    <h2 class="vg-kanban-modal__title" id="vg-bug-report-title">Segnala un problema</h2>' +
            '    <p class="vg-kanban-modal__message">Compila le tre pagine qui sotto. In fondo usa <strong>Avanti</strong> per proseguire e <strong>Invia</strong> sull’ultimo passo.</p>' +
            '    <p class="vg-bug-report-modal__status" data-status role="status"></p>' +
            '    <div class="vg-bug-report-modal__survey" id="vg-bug-report-survey"></div>' +
            '    <div class="vg-kanban-modal__actions vg-bug-report-modal__actions">' +
            '      <button type="button" class="vg-kanban-modal__btn" data-bug-report-close>Annulla</button>' +
            '      <button type="button" class="vg-kanban-modal__btn" data-bug-report-prev hidden>Indietro</button>' +
            '      <button type="button" class="vg-kanban-modal__btn vg-kanban-modal__btn--danger" data-submit>Avanti</button>' +
            '    </div>' +
            '  </div>' +
            '  <div class="vg-bug-report-modal__success" data-bug-report-success hidden>' +
            '    <h2 class="vg-bug-report-success__title" id="vg-bug-report-success-title">Segnalazione inviata</h2>' +
            '    <p class="vg-bug-report-success__subtitle">Grazie: abbiamo registrato il problema e lo esamineremo al più presto.</p>' +
            '    <div class="vg-bug-report-success__result" data-bug-report-result></div>' +
            '    <div class="vg-kanban-modal__actions vg-bug-report-success__actions">' +
            '      <button type="button" class="vg-kanban-modal__btn vg-kanban-modal__btn--danger" data-bug-report-close>Chiudi</button>' +
            '    </div>' +
            '  </div>' +
            '</div>';
        document.body.appendChild(wrap);

        var panel = wrap.querySelector('.vg-kanban-modal__panel');
        var formBlock = wrap.querySelector('[data-bug-report-form]');
        var successBlock = wrap.querySelector('[data-bug-report-success]');
        var resultEl = wrap.querySelector('[data-bug-report-result]');
        var statusEl = wrap.querySelector('[data-status]');
        var mount = wrap.querySelector('#vg-bug-report-survey');
        var submitBtn = wrap.querySelector('[data-submit]');
        var prevBtn = wrap.querySelector('[data-bug-report-prev]');
        var survey = null;

        function setStatus(msg, kind) {
            if (!statusEl) return;
            statusEl.textContent = msg || '';
            statusEl.classList.remove('is-error', 'is-ok');
            if (kind) statusEl.classList.add('is-' + kind);
        }

        function closeModal() {
            try {
                if (survey && typeof survey.dispose === 'function') {
                    survey.dispose();
                }
            } catch (eDispose) { /* ignore */ }
            setModalLock(false);
            document.removeEventListener('keydown', onEscapeKey, true);
            wrap.remove();
        }

        function onEscapeKey(e) {
            if (e.key === 'Escape' || e.key === 'Esc') {
                e.preventDefault();
                e.stopPropagation();
                closeModal();
            }
        }

        function bindCloseHandlers() {
            wrap.querySelectorAll('[data-bug-report-close]').forEach(function (btn) {
                btn.addEventListener('click', function (e) {
                    e.preventDefault();
                    e.stopPropagation();
                    closeModal();
                });
            });
        }
        bindCloseHandlers();

        wrap.addEventListener('click', function (e) {
            if (e.target === wrap) {
                closeModal();
            }
        });
        document.addEventListener('keydown', onEscapeKey, true);

        function showSuccess(res) {
            if (formBlock) formBlock.hidden = true;
            if (successBlock) successBlock.hidden = false;
            if (statusEl) {
                statusEl.textContent = '';
                statusEl.classList.remove('is-error', 'is-ok');
            }
            if (!resultEl) return;
            var iid = res && res.issue_iid != null ? String(res.issue_iid) : '';
            var html = '';
            if (iid) {
                html +=
                    '<p class="vg-bug-report-success__meta">Riferimento segnalazione <strong>#' + escHtml(iid) + '</strong></p>' +
                    '<p class="vg-bug-report-success__hint">Conservalo se ci contatti in merito al problema.</p>';
            } else {
                html = '<p class="vg-bug-report-success__meta">La segnalazione è stata registrata correttamente.</p>';
            }
            resultEl.innerHTML = html;
            panel.setAttribute('aria-labelledby', 'vg-bug-report-success-title');
        }

        _bugReportAttachmentFiles = [];

        survey = new Survey.Model(surveyJson());
        try {
            if (window.Survey.StylesManager && window.Survey.StylesManager.applyTheme) {
                window.Survey.StylesManager.applyTheme('defaultV2');
            }
        } catch (e0) {}
        wireSurveyFileHandlers(survey);
        survey.showCompleteButton = false;
        survey.render(mount);

        function syncFooterPrimaryLabel() {
            if (!submitBtn || !survey) return;
            submitBtn.textContent = survey.isLastPage ? 'Invia' : 'Avanti';
            if (prevBtn) {
                prevBtn.hidden = !!survey.isFirstPage;
            }
        }

        function validateCurrentPage() {
            if (typeof survey.validateCurrentPage === 'function') {
                return survey.validateCurrentPage();
            }
            return survey.validate();
        }

        survey.onCurrentPageChanged.add(function () {
            syncFooterPrimaryLabel();
            setStatus('');
        });
        syncFooterPrimaryLabel();

        if (prevBtn) {
            prevBtn.addEventListener('click', function (e) {
                e.preventDefault();
                e.stopPropagation();
                if (!survey.isFirstPage) {
                    survey.prevPage();
                    syncFooterPrimaryLabel();
                    setStatus('');
                }
            });
        }

        submitBtn.addEventListener('click', function () {
            if (!survey.isLastPage) {
                if (!validateCurrentPage()) {
                    setStatus('Compila i campi obbligatori in questa pagina.', 'error');
                    return;
                }
                setStatus('');
                survey.nextPage();
                syncFooterPrimaryLabel();
                return;
            }
            if (!survey.validate()) {
                setStatus('Compila i campi obbligatori.', 'error');
                return;
            }
            var data = survey.data || {};
            var fd = new FormData();
            fd.append('title', String(data.title || '').trim());
            fd.append('description', String(data.description || '').trim());
            fd.append('severity', String(data.severity || 'major'));
            fd.append('category', String(data.category || 'altro'));
            if (data.include_diagnostics !== false) {
                fd.append('context', JSON.stringify(collectContext()));
            }
            extractFilesFromSurvey(data).forEach(function (file) {
                fd.append('files', file, file.name || 'attachment');
            });

            submitBtn.disabled = true;
            setStatus('Invio in corso…');

            fetch(apiBase() + '/support/bug-reports', {
                method: 'POST',
                body: fd,
                credentials: 'include'
            })
                .then(function (r) {
                    return r.json().then(function (body) {
                        if (!r.ok) {
                            var d = body && body.detail;
                            throw new Error(typeof d === 'string' ? d : (d ? JSON.stringify(d) : 'Errore ' + r.status));
                        }
                        return body;
                    });
                })
                .then(function (res) {
                    showSuccess(res || {});
                })
                .catch(function (err) {
                    setStatus((err && err.message) || 'Invio non riuscito.', 'error');
                    submitBtn.disabled = false;
                });
        });

        setModalLock(true);
    }

    function init() {
        installDiagnostics();
        document.querySelectorAll('[data-vg-bug-report]').forEach(function (btn) {
            btn.addEventListener('click', function (e) {
                e.preventDefault();
                open();
            });
        });
    }

    window.VistaGareBugReport = {
        open: open,
        collectContext: collectContext,
        installDiagnostics: installDiagnostics
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})(window, document);
